import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import api, { ATR_FIBONACCI_SCAN } from '../../infrastructure/config/api';
import { watchlistService } from '../../application/services/watchlist-service';

const EMPTY_DATA = {
  timeframe: '15m',
  generatedAt: null,
  scannedSymbols: 0,
  totalSymbols: 0,
  skippedSymbols: 0,
  topBuy: [],
  topSell: [],
  parameters: {},
  marketContext: null,
  blockedLongSignals: 0,
  blockedShortSignals: 0,
  watchlistBuy: [],
  watchlistSell: [],
  rejectedBuy: [],
  rejectedSell: [],
  marketBlockedBuy: [],
  marketBlockedSell: [],
};

function formatDateTime(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '-';
}

function formatNumber(value) {
  return Number.isFinite(Number(value)) ? Number(value).toLocaleString(undefined, { maximumSignificantDigits: 8 }) : '-';
}

function isPassRow(row) {
  return row?.status === 'PASS'
    && row.reclaimDetected === true
    && row.bos === true
    && row.breakStructureDetected === true
    && row.marketFilter === 'PASS'
    && row.marketFilterPassed === true;
}

function isMarketBlockedRow(row) {
  return row?.technicalSetupPass === true
    && row.status === 'MARKET_BLOCKED'
    && row.reclaimDetected === true
    && row.bos === true
    && row.breakStructureDetected === true
    && row.marketFilter !== 'PASS';
}

function marketLabel(row) {
  if (row?.status === 'MARKET_BLOCKED' || row?.marketFilter === 'BLOCK') return 'BLOCKED';
  if (row?.marketPriority === 'PRIORITY') return `⭐ ${row.side === 'BUY' ? 'PRIORITY LONG' : 'PRIORITY SHORT'}`;
  if (row?.marketPriority === 'NORMAL') return row.side === 'BUY' ? 'LONG' : 'SHORT';
  return row?.marketLabel || row?.marketFilter || '-';
}

function statusColor(row) {
  if (row?.status === 'REJECTED') return 'error';
  if (row?.status === 'WATCH' || row?.status === 'MARKET_BLOCKED') return 'warning';
  return 'success';
}

function openTradingView(symbol) {
  const tradingViewSymbol = `BINANCE:${String(symbol || '').toUpperCase()}.P`;
  window.open(
    `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tradingViewSymbol)}`,
    '_blank',
    'noopener,noreferrer'
  );
}

function MarketAssetCard({ title, context, interpretations = [] }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 240, borderColor: '#27304a' }}>
      <Typography fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={`5m ${context?.['5m']?.trend || 'UNKNOWN'}`}
          color="info"
          variant="outlined"
        />
        <Chip
          size="small"
          label={`15m ${context?.['15m']?.trend || 'UNKNOWN'}`}
          color="info"
          variant="outlined"
        />
        <Chip
          size="small"
          label={`Last ${context?.lastSignal || '-'}`}
          color="info"
          variant="outlined"
        />
        {interpretations.map((item) => (
          <Chip key={item.label} size="small" label={item.label} color={item.color} />
        ))}
      </Stack>
    </Paper>
  );
}

function SignalActions({ row, watched, busy, onAdd, onRemove }) {
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title="Mở chart TradingView">
        <IconButton size="small" color="primary" onClick={() => openTradingView(row.symbol)}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={watched ? 'Xóa khỏi danh sách symbols' : 'Thêm vào danh sách symbols'}>
        <span>
          <IconButton
            size="small"
            color={watched ? 'error' : (row.side === 'BUY' ? 'success' : 'error')}
            disabled={busy}
            onClick={() => (watched ? onRemove(row) : onAdd(row))}
          >
            {busy
              ? <CircularProgress size={18} color="inherit" />
              : (watched ? <DeleteOutlineIcon fontSize="small" /> : <AddIcon fontSize="small" />)}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function MobileSignalCard({ row, watched, busy, onAdd, onRemove }) {
  return (
    <Paper sx={{ p: 1.5, background: '#121826', border: '1px solid #27304a' }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={700}>{row.symbol}</Typography>
          <Chip label={row.side} color={row.side === 'BUY' ? 'success' : 'error'} size="small" />
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip label={row.status || '-'} size="small" color={statusColor(row)} />
          {row.technicalSetupPass === true && <Chip label="TECHNICAL PASS" size="small" color="success" variant="outlined" />}
          <Chip label={`Retest #${row.retestNumber}`} size="small" variant="outlined" />
          <Chip label={`Wick ${row.wickRangePercent}%`} size="small" variant="outlined" />
          <Chip label={row.breakStructureDetected ? 'BOS ✓' : 'BOS ✕'} size="small" color={row.breakStructureDetected ? 'success' : 'error'} variant="outlined" />
          <Chip label={`Score ${row.score}`} size="small" variant="outlined" />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Pocket: {formatNumber(row.pocketLow)} – {formatNumber(row.pocketHigh)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          1.0: {formatNumber(row.level100)} | 0.5: {formatNumber(row.fib50)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Entry {formatNumber(row.entryPrice)} | SL {formatNumber(row.stopLoss)} | TP {formatNumber(row.takeProfit)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Trigger: {formatDateTime(row.triggerCandleTime)}
        </Typography>
        <Typography variant="caption" color={row.marketFilter === 'PASS' ? 'success.main' : 'error.main'}>
          Market: {marketLabel(row)}
        </Typography>
        <Typography variant="caption" color="text.secondary">BTC: {row.btcStatus || '-'}</Typography>
        <Typography variant="caption" color="text.secondary">BTCDOM: {row.btcDominanceStatus || '-'}</Typography>
        {(row.rejectReason || []).length > 0 && (
          <Typography variant="caption" color="error.main">
            Reason: {row.marketBlockReason || row.rejectReason.join(', ')}
          </Typography>
        )}
        {(row.reason || []).map((reason) => (
          <Typography key={reason} variant="caption" color="text.secondary">• {reason}</Typography>
        ))}
        <SignalActions row={row} watched={watched} busy={busy} onAdd={onAdd} onRemove={onRemove} />
      </Stack>
    </Paper>
  );
}

function SignalTable({ title, side, rows, mobile, watchedSymbols, busy, onAdd, onRemove }) {
  const sortedRows = [...(rows || [])].sort((a, b) => {
    const priorityDifference = Number(b.marketPriority === 'PRIORITY') - Number(a.marketPriority === 'PRIORITY');
    if (priorityDifference) return priorityDifference;
    const timeDifference = new Date(b.triggerCandleTime).getTime() - new Date(a.triggerCandleTime).getTime();
    return timeDifference || b.score - a.score;
  });

  return (
    <Paper sx={{ background: '#171c29', border: '1px solid #27304a', overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Chip label={`${sortedRows.length} kết quả`} color={side === 'BUY' ? 'success' : 'error'} variant="outlined" />
      </Stack>
      {mobile ? (
        <Stack spacing={1.25} sx={{ p: 1.25, pt: 0 }}>
          {sortedRows.map((row) => (
            <MobileSignalCard
              key={`${row.symbol}-${row.triggerCandleTime}`}
              row={row}
              watched={watchedSymbols.has(row.symbol)}
              busy={busy}
              onAdd={onAdd}
              onRemove={onRemove}
            />
          ))}
          {!sortedRows.length && <Typography color="text.secondary">Không có setup đạt đủ điều kiện.</Typography>}
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1550 }}>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Retest</TableCell>
                <TableCell>Wick</TableCell>
                <TableCell>BOS</TableCell>
                <TableCell>Technical</TableCell>
                <TableCell>Market</TableCell>
                <TableCell>BTC status</TableCell>
                <TableCell>BTCDOM status</TableCell>
                <TableCell>Reject reason</TableCell>
                <TableCell>Pocket 0.618–0.786</TableCell>
                <TableCell>Level 1.0 / 0.5</TableCell>
                <TableCell>Entry / SL / TP</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Lý do</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!sortedRows.length ? (
                <TableRow><TableCell colSpan={15} align="center">Không có setup đạt đủ điều kiện.</TableCell></TableRow>
              ) : sortedRows.map((row) => (
                <TableRow key={`${row.symbol}-${row.triggerCandleTime}`}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={700}>{row.symbol}</Typography>
                      <Chip label={row.side} color={row.side === 'BUY' ? 'success' : 'error'} size="small" />
                    </Stack>
                  </TableCell>
                  <TableCell>#{row.retestNumber}</TableCell>
                  <TableCell>{row.wickRangePercent}% ({row.wickToBody}x)</TableCell>
                  <TableCell>{row.bos ? 'PASS' : 'NO_BOS'}</TableCell>
                  <TableCell>{row.technicalSetupPass === true ? 'PASS' : '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={marketLabel(row)}
                      color={row.marketFilter === 'PASS' ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.btcStatus || '-'}</TableCell>
                  <TableCell>{row.btcDominanceStatus || '-'}</TableCell>
                  <TableCell>
                    {row.marketBlockReason || ((row.rejectReason || []).length ? row.rejectReason.join(', ') : '-')}
                  </TableCell>
                  <TableCell>{formatNumber(row.pocketLow)} – {formatNumber(row.pocketHigh)}</TableCell>
                  <TableCell>{formatNumber(row.level100)} / {formatNumber(row.fib50)}</TableCell>
                  <TableCell>{formatNumber(row.entryPrice)} / {formatNumber(row.stopLoss)} / {formatNumber(row.takeProfit)}</TableCell>
                  <TableCell>{formatDateTime(row.triggerCandleTime)}</TableCell>
                  <TableCell>
                    {(row.reason || []).map((reason) => (
                      <Typography key={reason} variant="caption" display="block" color="text.secondary">• {reason}</Typography>
                    ))}
                  </TableCell>
                  <TableCell align="right">
                    <SignalActions
                      row={row}
                      watched={watchedSymbols.has(row.symbol)}
                      busy={busy}
                      onAdd={onAdd}
                      onRemove={onRemove}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

const AtrFibonacciScanPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [watchedSymbols, setWatchedSymbols] = useState(new Set());
  const [actionLoadingKey, setActionLoadingKey] = useState('');

  const applyPayload = useCallback((payload = {}) => {
    setData({
      ...EMPTY_DATA,
      ...payload,
      topBuy: (payload.topBuy || []).filter(isPassRow),
      topSell: (payload.topSell || []).filter(isPassRow),
      marketBlockedBuy: (payload.marketBlockedBuy || []).filter(isMarketBlockedRow),
      marketBlockedSell: (payload.marketBlockedSell || []).filter(isMarketBlockedRow),
      rejectedBuy: (payload.rejectedBuy || []).filter((row) => row.status !== 'MARKET_BLOCKED'),
      rejectedSell: (payload.rejectedSell || []).filter((row) => row.status !== 'MARKET_BLOCKED'),
    });
    setIsScanning(payload.scan?.status === 'running');
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(ATR_FIBONACCI_SCAN.LATEST);
      applyPayload(response.data?.data);
      setScanError('');
    } catch (error) {
      const message = error.response?.data?.error || 'Không thể tải kết quả ATR Fibonacci scan';
      setScanError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [applyPayload, enqueueSnackbar]);

  useEffect(() => {
    loadLatest();
    watchlistService.list()
      .then((items) => setWatchedSymbols(new Set(items.map((item) => String(item.symbol).toUpperCase()))))
      .catch(() => {});
  }, [loadLatest]);

  useEffect(() => {
    if (!isScanning) return undefined;
    const intervalId = setInterval(async () => {
      try {
        const response = await api.get(ATR_FIBONACCI_SCAN.LATEST);
        const payload = response.data?.data || {};
        if (payload.scan?.status === 'running') return;
        applyPayload(payload);
        if (payload.scan?.status === 'completed') enqueueSnackbar('ATR Fibonacci scan hoàn tất', { variant: 'success' });
        if (payload.scan?.status === 'failed') setScanError(payload.scan?.error || 'Scan thất bại');
      } catch (error) {
        console.error('Failed to poll ATR Fibonacci scan:', error);
      }
    }, 2000);
    return () => clearInterval(intervalId);
  }, [applyPayload, enqueueSnackbar, isScanning]);

  const runScan = useCallback(async () => {
    try {
      setIsScanning(true);
      const response = await api.post(ATR_FIBONACCI_SCAN.RUN);
      setScanError('');
      enqueueSnackbar(response.data?.accepted ? 'Scan đã bắt đầu chạy nền' : 'Scan đang chạy', { variant: 'info' });
    } catch (error) {
      setIsScanning(false);
      const message = error.response?.data?.error || 'Không thể chạy scan';
      setScanError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const addToWatchlist = useCallback(async (row) => {
    if (watchedSymbols.has(row.symbol)) return;
    try {
      setActionLoadingKey(row.symbol);
      await watchlistService.add(row.symbol, row.side === 'BUY' ? 'LONG' : 'SHORT', 'ATR_FIB_FIRST_RETEST');
      setWatchedSymbols((current) => new Set(current).add(row.symbol));
      enqueueSnackbar(`Đã thêm ${row.symbol} vào danh sách symbols`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Không thể thêm symbol', { variant: 'error' });
    } finally {
      setActionLoadingKey('');
    }
  }, [enqueueSnackbar, watchedSymbols]);

  const removeFromWatchlist = useCallback(async (row) => {
    try {
      setActionLoadingKey(row.symbol);
      await watchlistService.remove(row.symbol);
      setWatchedSymbols((current) => {
        const next = new Set(current);
        next.delete(row.symbol);
        return next;
      });
      enqueueSnackbar(`Đã xóa ${row.symbol} khỏi danh sách symbols`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Không thể xóa symbol', { variant: 'error' });
    } finally {
      setActionLoadingKey('');
    }
  }, [enqueueSnackbar]);

  const commonTableProps = {
    mobile: isMobile,
    watchedSymbols,
    busy: Boolean(actionLoadingKey),
    onAdd: addToWatchlist,
    onRemove: removeFromWatchlist,
  };
  const longPriority = data.marketContext?.btcDominanceDirection === 'BEAR' ? 'PRIORITY' : 'NORMAL';
  const shortPriority = data.marketContext?.btcDominanceDirection === 'BULL' ? 'PRIORITY' : 'NORMAL';

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      <Stack spacing={3}>
        <Paper sx={{ p: { xs: 2, md: 3 }, background: 'linear-gradient(135deg, #111827 0%, #182235 100%)', border: '1px solid #27304a' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>ATR Fibonacci First-Retest Scanner</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                RMA(100) + ATR(100)×3 | BTC quyết định hướng · BTCDOM chỉ xếp priority
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Chip label={`${data.timeframe} · ${data.scannedSymbols}/${data.totalSymbols} symbols`} variant="outlined" />
              <Chip label={`Updated ${formatDateTime(data.generatedAt)}`} variant="outlined" />
              <Button variant="contained" startIcon={isScanning ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />} disabled={isScanning} onClick={runScan} fullWidth={isMobile}>
                {isScanning ? 'Scanning...' : 'Scan'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <MarketAssetCard
            title="BTC · PRIMARY DIRECTION"
            context={data.marketContext?.btc}
            interpretations={[
              { label: `LONG DIRECTION: ${data.marketContext?.longAllowed ? 'PASS' : 'BLOCK'}`, color: data.marketContext?.longAllowed ? 'success' : 'warning' },
              { label: `SHORT DIRECTION: ${data.marketContext?.shortAllowed ? 'PASS' : 'BLOCK'}`, color: data.marketContext?.shortAllowed ? 'success' : 'warning' },
            ]}
          />
          <MarketAssetCard
            title="BTCDOM · PRIORITY ONLY"
            context={data.marketContext?.btcDominance}
            interpretations={[
              { label: `ALT LONG PRIORITY: ${longPriority === 'PRIORITY' ? '⭐ PRIORITY' : 'NORMAL'}`, color: longPriority === 'PRIORITY' ? 'success' : 'default' },
              { label: `ALT SHORT PRIORITY: ${shortPriority === 'PRIORITY' ? '⭐ PRIORITY' : 'NORMAL'}`, color: shortPriority === 'PRIORITY' ? 'success' : 'default' },
            ]}
          />
        </Stack>

        <Alert severity={!data.marketContext ? 'info' : (data.marketContext.longAllowed ? 'success' : 'warning')}>
          {!data.marketContext
            ? 'Chưa có market context. Hãy chạy Scan để kiểm tra BTC và BTCDOM.'
            : (data.marketContext.longAllowed
              ? `BTC direction PASS cho LONG · BTCDOM xếp mức ${longPriority}.`
              : `BTC direction BLOCK cho LONG: setup kỹ thuật vẫn được hiển thị riêng${data.blockedLongSignals ? ` (${data.blockedLongSignals} setup bị chặn)` : ''}.`)}
        </Alert>

        <Alert severity={!data.marketContext ? 'info' : (data.marketContext.shortAllowed ? 'success' : 'warning')}>
          {!data.marketContext
            ? 'Chưa có market context cho SHORT.'
            : (data.marketContext.shortAllowed
              ? `BTC direction PASS cho SHORT · BTCDOM xếp mức ${shortPriority}.`
              : `BTC direction BLOCK cho SHORT${data.blockedShortSignals ? ` (${data.blockedShortSignals} setup bị chặn)` : ''}.`)}
        </Alert>

        {scanError && <Alert severity="warning" action={<Button color="inherit" onClick={loadLatest}>Thử lại</Button>}>{scanError}</Alert>}
        {isLoading ? (
          <Box sx={{ py: 12, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={3}>
            <SignalTable title="LONG · RED / TRUST / BILL structure" side="BUY" rows={data.topBuy} {...commonTableProps} />
            <SignalTable title="SHORT · First retest upper-wick reclaim" side="SELL" rows={data.topSell} {...commonTableProps} />
            {data.marketBlockedBuy.length > 0 && (
              <SignalTable title="LONG · TECHNICAL PASS / MARKET BLOCKED" side="BUY" rows={data.marketBlockedBuy} {...commonTableProps} />
            )}
            {data.marketBlockedSell.length > 0 && (
              <SignalTable title="SHORT · TECHNICAL PASS / MARKET BLOCKED" side="SELL" rows={data.marketBlockedSell} {...commonTableProps} />
            )}
            {data.watchlistBuy.length > 0 && (
              <SignalTable title="LONG WATCHLIST · chờ reclaim / BOS" side="BUY" rows={data.watchlistBuy} {...commonTableProps} />
            )}
            {data.watchlistSell.length > 0 && (
              <SignalTable title="SHORT WATCHLIST · chờ reclaim / BOS" side="SELL" rows={data.watchlistSell} {...commonTableProps} />
            )}
            {data.rejectedBuy.length > 0 && (
              <SignalTable title="LONG REJECTED" side="BUY" rows={data.rejectedBuy} {...commonTableProps} />
            )}
            {data.rejectedSell.length > 0 && (
              <SignalTable title="SHORT REJECTED" side="SELL" rows={data.rejectedSell} {...commonTableProps} />
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default AtrFibonacciScanPage;
